import { warnAndNotify } from '@execonline-inc/error-reporting.private';
import { URLParser, putQueryParam, toUrlT } from '@execonline-inc/url';
import { Task } from 'taskarian';
import { ParticipantsError } from '.';
import { callApi } from '../../Appy';
import { currentUserStore } from '../../CurrentUserStore';
import { handleError } from '../../ErrorActionableReaction';
import { findLinkT } from '../../LinkyLinky';
import { SearchParameters } from '../../ListFilter/Types';
import ParticipantStore from '../../ParticipantStore';
import { Link } from '../../Resource/Types';
import { participantsResourceDecoder } from '../Decoders';
import { LoadedMore, Loading, LoadingMore, ParticipantsResource } from '../Types';
import ParticipantsStore from '../index';

const handleParticipantsError = (store: ParticipantsStore) => (error: ParticipantsError) => {
  switch (error.kind) {
    case 'missing-link-error':
    case 'invalid-url-error':
      store.error('We are unable to process your request at this time');
      warnAndNotify('ParticipantsReactions', error.kind, error);
      break;
    default:
      handleError(store, error);
  }
};

const updateSearchParams = (searchParameters: SearchParameters) => (url: URLParser) =>
  searchParameters.reduce(
    (acc, parameter) =>
      putQueryParam(`participants_filter[${parameter.key}]`, parameter.value)(acc),
    url,
  );

const buildUrl = (state: Loading | LoadingMore, link: Link): Task<ParticipantsError, Link> =>
  toUrlT(link.href)
    .map(updateSearchParams(state.searchParameters))
    .map(putQueryParam('page', String(state.page)))
    .map((url): Link => ({ ...link, href: url.href }));

const requestParticipantsResource = (state: Loading | LoadingMore) => (link: Link) =>
  buildUrl(state, link).andThen(callApi(participantsResourceDecoder, {}));

const initializeParticipantStores = (store: ParticipantsStore, resource: ParticipantsResource) =>
  resource.payload.participants.map((resource) => new ParticipantStore(store, resource));

const addMoreParticipantStores =
  (state: LoadingMore, store: ParticipantsStore) =>
  (newResource: ParticipantsResource): LoadedMore => ({
    resource: newResource,
    participantStores: state.participantStores.concat(
      initializeParticipantStores(store, newResource),
    ),
  });

const concatParticipants =
  (store: ParticipantsStore, state: LoadingMore) => (newResource: ParticipantsResource) =>
    Task.succeed<ParticipantsError, ParticipantsResource>(newResource).map(
      addMoreParticipantStores(state, store),
    );

const getParticipants = (state: Loading | LoadingMore) =>
  Task.succeed<ParticipantsError, ReadonlyArray<Link>>(currentUserStore.links)
    .andThen(findLinkT('participants'))
    .andThen(requestParticipantsResource(state));

export const load = (store: ParticipantsStore, state: Loading) =>
  getParticipants(state).fork(handleParticipantsError(store), (resource) =>
    store.ready(resource, initializeParticipantStores(store, resource)),
  );

export const loadMore = (store: ParticipantsStore, state: LoadingMore) =>
  getParticipants(state)
    .andThen(concatParticipants(store, state))
    .fork(handleParticipantsError(store), (loadedMore) =>
      store.ready(loadedMore.resource, loadedMore.participantStores),
    );
